import { useMemo } from 'react';
import {
  infiniteQueryOptions,
  useQueries,
  useQuery,
} from '@tanstack/react-query';
import {
  handleGetLineageGeometriesGeometryIdLineageGetOptions,
  handleListMeshesMeshesGetOptions,
  handleListStudiesStudiesGetInfiniteQueryKey,
  handleListStudiesStudiesGetOptions,
} from '@/api/generated/@tanstack/react-query.gen';
import { handleListStudiesStudiesGet } from '@/api/generated/sdk.gen';
import type {
  ListStudiesResponse,
  MeshResponse,
  MeshStatus,
  StudyResponse,
  StudyStatus,
} from '@/api/generated/types.gen';
import { statusTone, statusInProgress, type Tone } from '@/lib/statusTone';
import { TIMEOUTS } from '@/constants';
import { studyPointProgress } from '@/routes/_app/simops/-data/study-progress';

/**
 * Mesh states that mean "this job is still going". `pending` is included: from
 * the reader's side a job that has been accepted but not picked up is running,
 * and splitting it out would put the same mesh in neither list.
 */
const RUNNING_MESH_STATUSES: ReadonlySet<MeshStatus> = new Set([
  'pending',
  'meshing',
  'recombining',
]);

/**
 * The three study states that are waiting on a person. Every one of them is
 * spelled `awaiting_*` on the wire and resolves to `warning` in `statusTone`
 * for exactly this reason — the study has stopped and will not move until
 * somebody opens it.
 */
const BLOCKED_STUDY_STATUSES: ReadonlySet<StudyStatus> = new Set([
  'awaiting_user_meshing',
  'awaiting_bc_review',
  'awaiting_mobo_review',
]);

const RUNNING_STUDY_STATUSES: ReadonlySet<StudyStatus> = new Set([
  'preprocessing',
  'running',
]);

/** How many rows each list shows before it stops. */
const MAX_ROWS = 4;

/**
 * How many mesh rows per list get a lineage read.
 *
 * Wider than `MAX_ROWS` on purpose. A mesh whose lineage has not resolved is
 * held back (it has no name to show), so budgeting exactly `MAX_ROWS` would
 * mean four unresolvable rows leave the list EMPTY while resolvable ones
 * below them were never looked up. Double the budget absorbs that without
 * making the fan-out unbounded.
 */
const MESH_RESOLVE_BUDGET = MAX_ROWS * 2;

/**
 * Page size for the two list reads the home page makes.
 *
 * Deliberately one big page rather than a small one: `GET /studies` orders by
 * `created_at`, so a `limit: 6` returns the six newest STUDIES — not the six
 * most recently touched. Anything that sorts on `updated_at` (the recents
 * grid) has to sort over a page wide enough to contain the answer.
 */
export const HOME_PAGE_LIMIT = 100;

/**
 * The studies page both home cards read. Shared so they hit ONE cache entry:
 * two limits would mean two polls of the same list, and the smaller one would
 * be answering a question its ordering cannot answer.
 */
export const homeStudiesQueryOptions = () =>
  handleListStudiesStudiesGetOptions({ query: { limit: HOME_PAGE_LIMIT } });

/**
 * The cursor-paginated form of the same list, for a reader that pages
 * through studies rather than showing only the newest page. The home cards
 * keep the single page above: they only ever show the top of a `created_at`
 * ordering.
 *
 * Its own cache entry (the generated infinite key), not the page's: an
 * `InfiniteData` shape under the plain key would break every reader of it.
 *
 * Its one caller today is the SimOps / EvalTool landings, which pass
 * `STUDIES_PAGE_LIMIT` (20) so they page as the reader scrolls. The page size
 * is required: it is part of the cache key, so a caller must choose it.
 */
export function listStudiesInfiniteOptions(limit: number) {
  return infiniteQueryOptions({
    queryKey: handleListStudiesStudiesGetInfiniteQueryKey({ query: { limit } }),
    queryFn: async ({ pageParam, signal }): Promise<ListStudiesResponse> => {
      const { data } = await handleListStudiesStudiesGet({
        query: { cursor: pageParam ?? undefined, limit },
        signal,
        throwOnError: true,
      });
      return data;
    },
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    initialPageParam: null as string | null,
  });
}

/**
 * How a card made of two independent reads reports failure.
 *
 * The rule both home cards need, written once: an error message REPLACES the
 * content, so it may only be shown when there is no content — one list 5xx-ing
 * must not hide the other list's rows. When exactly one source is down the
 * card renders what it has and says so, because a partial answer passed off as
 * the whole picture is the failure mode this shape exists to avoid. `GET
 * /meshes` returning 5xx is not hypothetical; `useDashboardGeometries` carries
 * a backoff built for it.
 */
export function combineFetchState(
  aFailed: boolean,
  bFailed: boolean,
  opts: { alsoPartial?: boolean } = {},
): { isError: boolean; isPartial: boolean } {
  return {
    isError: aFailed && bFailed,
    isPartial: aFailed !== bFailed || opts.alsoPartial === true,
  };
}

export type HomeTarget =
  | { kind: 'geometry'; geometryId: string; meshId: string | null }
  | { kind: 'study'; studyId: string };

export interface HomeWorkItem {
  key: string;
  /** What the reader calls this thing — a file name or a study name. */
  name: string;
  target: HomeTarget;
  tone: Tone;
  /** Whether the status dot pulses, i.e. work is actually in flight. */
  pulse: boolean;
  /**
   * Progress on the right of the row, or null when there is none to state.
   * Studies carry `finished/total` points (failed and cancelled count as
   * finished); a mesh job reports no progress and
   * no estimate, so its rows leave this empty rather than invent a countdown.
   */
  detail: string | null;
  /**
   * The mesh this row is a RETRY DECISION about, or null for every other row.
   *
   * Only a blocked mesh has one, and only such a row can be put to the
   * mesh-retry advisor — which is what the Ask box's lead suggestion does
   * (ENG-2028). A blocked STUDY is waiting on boundary conditions or a review,
   * not on a retry verdict, so asking the retry advisor about it would be a
   * question with no answer.
   */
  retryMeshId: string | null;
}

export interface HomeWork {
  /** Stopped, and will not move until this reader does something. */
  blocked: HomeWorkItem[];
  /** Moving on its own. */
  running: HomeWorkItem[];
  /**
   * Everything blocked, including what `blocked` was truncated past. The
   * header's count reads this: saying "4 on you" when ten things are waiting
   * is the one number on this card a reader would act on, and the truncated
   * array cannot tell them.
   */
  blockedTotal: number;
  isLoading: boolean;
  /** Nothing could be read at all. */
  isError: boolean;
  /**
   * One of the two lists failed while the other answered. The rows shown are
   * real but incomplete — said out loud rather than passed off as the whole
   * picture, because `GET /meshes` 5xx-ing is a case the app already expects.
   */
  isPartial: boolean;
}

/**
 * A mesh row's geometry, resolved to something a reader recognises.
 *
 * `GET /meshes` returns the geometry's id and nothing else, and `GET
 * /geometries` pages over lineage ROOTS — so a mesh built against an
 * auto-repaired v2 names a geometry that list will never contain. The lineage
 * read is what closes that gap, and it also hands back the root id, which is
 * what `/meshgen/$uuid` expects in its path.
 */
interface ResolvedGeometry {
  rootId: string;
  filename: string;
}

/**
 * Meshes that belong on the home page at all, newest first.
 *
 * `blockedCount` is the full total; `blocked` is only as many as get a lineage
 * read. The two differ, and the difference is what the header's count needs —
 * a mesh nobody looked up is still a mesh waiting on the reader.
 */
function homeMeshes(meshes: MeshResponse[]): {
  blocked: MeshResponse[];
  blockedCount: number;
  running: MeshResponse[];
} {
  const blocked: MeshResponse[] = [];
  const running: MeshResponse[] = [];
  for (const mesh of meshes) {
    if (mesh.geometry_id == null) continue;
    if (mesh.retry_needs_attention === true) blocked.push(mesh);
    else if (RUNNING_MESH_STATUSES.has(mesh.status)) running.push(mesh);
  }
  return {
    blocked: blocked.slice(0, MESH_RESOLVE_BUDGET),
    blockedCount: blocked.length,
    running: running.slice(0, MESH_RESOLVE_BUDGET),
  };
}

function studyItem(study: StudyResponse): HomeWorkItem {
  const tone = statusTone('study', study.status);
  const { total, finished } = studyPointProgress(study);
  return {
    key: `study:${study.id}`,
    name: study.name,
    target: { kind: 'study', studyId: study.id },
    tone,
    pulse: statusInProgress('study', study.status),
    detail: total > 0 ? `${finished}/${total}` : null,
    retryMeshId: null,
  };
}

function meshItem(
  mesh: MeshResponse,
  geometry: ResolvedGeometry,
  blocked: boolean,
): HomeWorkItem {
  // A mesh waiting on a retry decision is amber whatever its own status says:
  // the row is about the decision, and `failed` (which is what such a mesh
  // usually is) would paint it red as if nothing could be done about it.
  const tone = blocked ? 'warning' : statusTone('mesh', mesh.status);
  return {
    key: `mesh:${mesh.id}`,
    name: geometry.filename,
    target: {
      kind: 'geometry',
      geometryId: geometry.rootId,
      meshId: mesh.id,
    },
    tone,
    pulse: !blocked && statusInProgress('mesh', mesh.status),
    detail: null,
    retryMeshId: blocked ? mesh.id : null,
  };
}

/**
 * What is waiting on this reader, and what is running without them (ENG-2028).
 *
 * Reads only what today's API can answer. There is no queue-position or
 * finish-time endpoint, so no row claims one.
 */
export function useHomeWork(): HomeWork {
  const meshesQuery = useQuery({
    ...handleListMeshesMeshesGetOptions({ query: { limit: HOME_PAGE_LIMIT } }),
    refetchInterval: (query) => {
      const items = query.state.data?.items ?? [];
      return items.some((m) => RUNNING_MESH_STATUSES.has(m.status))
        ? TIMEOUTS.DASHBOARD_POLL_INTERVAL
        : false;
    },
  });

  const studiesQuery = useQuery({
    ...homeStudiesQueryOptions(),
    refetchInterval: (query) => {
      const items = query.state.data?.items ?? [];
      return items.some((s) => RUNNING_STUDY_STATUSES.has(s.status))
        ? TIMEOUTS.STUDY_POLL_INTERVAL
        : false;
    },
  });

  const meshes = useMemo(
    () => homeMeshes(meshesQuery.data?.items ?? []),
    [meshesQuery.data],
  );

  // One lineage read per DISTINCT geometry behind a mesh row we might show:
  // at most `2 * MESH_RESOLVE_BUDGET` (16), since the blocked and running
  // lists are budgeted separately, and in practice far fewer — a blocked mesh
  // and its successor usually share a geometry, and `staleTime` keeps a
  // resolved one resolved.
  const geometryIds = useMemo(() => {
    const ids = [...meshes.blocked, ...meshes.running]
      .map((m) => m.geometry_id)
      .filter((id): id is string => id != null);
    return [...new Set(ids)];
  }, [meshes]);

  const lineageQueries = useQueries({
    queries: geometryIds.map((geometryId) => ({
      ...handleGetLineageGeometriesGeometryIdLineageGetOptions({
        path: { geometry_id: geometryId },
      }),
      staleTime: 30_000,
    })),
  });

  // Built inline rather than memoised. `useQueries` hands back a fresh array
  // every render, so a memo over it needs a synthesised key AND the
  // exhaustive-deps escape hatch — about thirty lines and a lint suppression
  // to avoid rebuilding a map of at most `MESH_RESOLVE_BUDGET` entries on a
  // page with no render pressure. The loop is cheaper than the machinery.
  const geometryById = new Map<string, ResolvedGeometry>();
  let lineageFailed = false;
  lineageQueries.forEach((query, i) => {
    const geometryId = geometryIds[i];
    if (!geometryId) return;
    // A lineage read that ERRORED is not the same as one still in flight: the
    // row it names will never appear, so it has to reach `isPartial` rather
    // than vanish. Every other failure on this card is spoken aloud; a mesh
    // silently missing from "waiting on you" is the worst one to swallow.
    if (query.isError) lineageFailed = true;
    const data = query.data;
    if (!data) return;
    // Name the row after the version the mesh was actually built from — that
    // is the file the reader chose. Versions come back oldest-first, so the
    // root is the fallback when the requested version has since been deleted.
    const version =
      data.versions.find((v) => v.id === geometryId) ?? data.versions[0];
    if (!version) return;
    geometryById.set(geometryId, {
      rootId: data.root_geometry_id,
      filename: version.filename,
    });
  });

  const studies = studiesQuery.data?.items ?? [];

  const toMeshItems = (rows: MeshResponse[], blocked: boolean) =>
    rows.flatMap((mesh) => {
      const geometry =
        mesh.geometry_id == null
          ? undefined
          : geometryById.get(mesh.geometry_id);
      // A row with no name yet is held back rather than shown as "Untitled":
      // its lineage read is still in flight, and it appears a tick later.
      return geometry ? [meshItem(mesh, geometry, blocked)] : [];
    });

  const blockedStudies = studies.filter((s) =>
    BLOCKED_STUDY_STATUSES.has(s.status),
  );

  // Meshes first, studies after — but both lists are built in FULL and only
  // then truncated. Capping the mesh half before the merge meant four blocked
  // meshes hid every blocked study behind them.
  const blocked = [
    ...toMeshItems(meshes.blocked, true),
    ...blockedStudies.map(studyItem),
  ].slice(0, MAX_ROWS);

  const running = [
    ...toMeshItems(meshes.running, false),
    ...studies
      .filter((s) => RUNNING_STUDY_STATUSES.has(s.status))
      .map(studyItem),
  ].slice(0, MAX_ROWS);

  return {
    blocked,
    running,
    blockedTotal: meshes.blockedCount + blockedStudies.length,
    isLoading: meshesQuery.isLoading || studiesQuery.isLoading,
    ...combineFetchState(meshesQuery.isError, studiesQuery.isError, {
      // A lineage read failing counts as partial too: the mesh row it named is
      // gone from the list, and the reader has no other way to learn that.
      alsoPartial: lineageFailed,
    }),
  };
}
